import {clamp, json, texture} from "p3";
import common from "../common";
import definitions from "../definitions";
import settings from "../settings";
import LocationButton from "../ui/map/LocationButton";
import TutorialMapControls from "../ui/tutorial/TutorialMapControls";
import {locale, resizeLabelToFitText} from "../utils";
import GameScene from "./GameScene";
import MenuScene from "./MenuScene";

export default class MapScene extends MenuScene {
    constructor() {
        super("map");

        this._camera = null;
        this._mapLayer = null;
        this._buttonLayer = null;

        this._mapWidth = 0;
        this._mapHeight = 0;

        this._cameraTarget = new PIXI.Point();
        this._cameraTargetEase = 12;

        this._mapDragEnabled = false;
        this._mapDragOffset = new PIXI.Point();

        this._mapDragDistanceCovered = 0;

        this._pin = null;

        this._defaultTween = null;

        this._tutorial = null;

        this._areaLabel1 = null;
        this._areaLabel2 = null;
        this._areaLabel3 = null;

        this._tempButton1 = null;
        this._tempButton2 = null;

        this._closeButton = null;
    }

    init() {
        super.init();
    }

    onSceneLoaded() {
        super.onSceneLoaded();

        if (!common.saveData.settings.musicMute) {
            P3.audio.playMusic("music_map_loop");
        }
        P3.audio.playSound(['vo_iv_hmm_00', 'vo_iv_hmm_01', 'vo_iv_hmm_02']);

        this.interactive = true;

        this.createFill();

        this._camera = new P3.Camera(new PIXI.Point(common.STAGE_WIDTH * 0.5, P3.View.height * 0.5), new PIXI.Point(0, 0));
        this._camera.zoom = 1.3;

        this._mapLayer = this._addEmptyLayer(new PIXI.Point(1, 1));
        this._buttonLayer = this._addEmptyLayer(new PIXI.Point(1, 1));

        const map = new PIXI.Sprite(texture("map"));
        this._mapLayer.view.addChild(map);

        this._mapWidth = map.texture.width;
        this._mapHeight = map.texture.height;

        this._areaLabel1 = new PIXI.BitmapText(locale('level_svan_land').toUpperCase(), {
            fontName: "Alghera Pro",
            fontSize: 54,
            align: "center",
            tint: 0x0
        });
        this._areaLabel1.position.set(970, 848);
        this._areaLabel1.anchor.set(0.5);
        map.addChild(this._areaLabel1);

        this._areaLabel2 = new PIXI.BitmapText(locale('level_kingdom_stag'), {
            fontName: "Alghera Pro",
            fontSize: 42,
            align: "center",
            tint: 0x0,
            maxWidth: 400
        });
        this._areaLabel2.position.set(300, 970);
        this._areaLabel2.anchor.set(0.5);
        map.addChild(this._areaLabel2);

        this._areaLabel3 = new PIXI.BitmapText(locale('level_forest_outlaws'), {
            fontName: "Alghera Pro",
            fontSize: 40,
            align: "center",
            tint: 0x0,
            maxWidth: 200
        });
        this._areaLabel3.position.set(1664, 608);
        this._areaLabel3.anchor.set(0.5);
        map.addChild(this._areaLabel3);

        this._camera.target.x = this._cameraTarget.x = map.width * 0.5;
        this._camera.target.y = this._cameraTarget.y = map.height * 0.5;

        const locations = [
            definitions.LEVEL_ID.CASTLE_DUNGEON,
            definitions.LEVEL_ID.CASTLE_EXTERIOR,
            definitions.LEVEL_ID.CAVE_HIDDEN_1,
            definitions.LEVEL_ID.CAVE_HIDDEN_2,
            definitions.LEVEL_ID.CAVE_HIDDEN_3,
            definitions.LEVEL_ID.CAVE_HIDDEN_4,
            definitions.LEVEL_ID.CAVE_HIDDEN_5,
            definitions.LEVEL_ID.CAVE_HIDDEN_6,
            definitions.LEVEL_ID.CAVE_MOLES,
            definitions.LEVEL_ID.FOREST_BOARIS_CAMP,
            definitions.LEVEL_ID.FOREST_CLEARING,
            definitions.LEVEL_ID.FOREST_DARK,
            definitions.LEVEL_ID.FOREST_LAKESIDE_EAST,
            definitions.LEVEL_ID.FOREST_LAKESIDE_WEST,
            definitions.LEVEL_ID.FOREST_MOLECAVE,
            definitions.LEVEL_ID.FOREST_OUTLAWS,
            definitions.LEVEL_ID.FOREST_SWAMP,
            definitions.LEVEL_ID.GNOME_CLEARING,
            definitions.LEVEL_ID.KINGDOM_STAG,
            definitions.LEVEL_ID.ROCKY_MOUNTAIN,
            definitions.LEVEL_ID.SVAN_LAND,
            definitions.LEVEL_ID.UNDERWATER_KINGDOM,
            definitions.LEVEL_ID.VILLAGE_FARM,
            definitions.LEVEL_ID.TUTORIAL
        ];

        for (let i = locations.length-1; i >= 0; i--) {
            if (json(locations[i]).metadata.phase > settings.PHASE) {
                locations.splice(i, 1);
            }
        }

        if(locations.indexOf(definitions.LEVEL_ID.CAVE_HIDDEN_1) > -1 && !GameScene.active.questManager.isQuestComplete('quest_1', 'suzie')) {
            locations.splice(locations.indexOf(definitions.LEVEL_ID.CAVE_HIDDEN_1), 1);
        }
        if(locations.indexOf(definitions.LEVEL_ID.CAVE_HIDDEN_2) > -1 && !GameScene.active.questManager.isQuestComplete('quest_1', 'chick_of_doom')) {
            locations.splice(locations.indexOf(definitions.LEVEL_ID.CAVE_HIDDEN_2), 1);
        }

        this._parseLocations(locations);

        this._closeButton = P3.Button.create(
            texture("btn_small_out"),
            null,
            null,
            texture("btn_icon_cross")
        );
        this._closeButton.overSoundName = "sfx_btn_generic_rollover";
        this._closeButton.clickSoundName = "sfx_btn_map_close";
        this._closeButton.signals.click.add(this.onCloseButtonClick, this);
        this.addChild(this._closeButton);

        this.createTitleBanner(locale('ui_map_title'));

        if(common.saveData.activeSlot.player.mapTutorial) {
            this._tutorial = new TutorialMapControls();
            this._tutorial.x = (common.STAGE_WIDTH / 2) + (P3.View.width / 2) - 200;
            this._tutorial.y = (common.STAGE_HEIGHT / 2) + (P3.View.height / 2) - 200;
            this.addChild(this._tutorial);
        }

        this.resize();
    }

    destroy() {
        this.removeTween(this._defaultTween);

        this._closeButton.destroy();

        P3.audio.stopMusic();

        if (this._tutorial) {
            this._tutorial.destroy();
        }

        super.destroy();
    }

    resize() {
        super.resize();

        if (this.isSceneLoaded) {
            this._closeButton.position.set(
                (common.STAGE_WIDTH + P3.View.width - this._closeButton.width) * 0.5 - this.buttonMargin.width,
                this._closeButton.height * 0.5 + this.buttonMargin.y
            );
        }
    }

    update() {
        super.update();

        if (this.isSceneLoaded) {
            this.updateCamera();

            if(this._tutorial && this._mapDragDistanceCovered > 1000) {
                this._tutorial.destroy();
                this._tutorial = null;
                common.saveData.activeSlot.player.mapTutorial = false;
                common.saveData.saveGame();
            }
        }
    }

    updateCamera() {
        const touch = P3.input.touch;
        if (touch) {
            if (!this._mapDragEnabled) {
                this._mapDragOffset = new PIXI.Point(touch.x + this._camera.target.x, touch.y + this._camera.target.y);
            }

            this._cameraTarget.x = this._mapDragOffset.x - touch.x;
            this._cameraTarget.y = this._mapDragOffset.y - touch.y;
        }
        this._mapDragEnabled = touch;

        const zoom      = 1 / this._camera.zoom;
        const left      = P3.View.center.x * zoom;
        const right     = this._mapWidth - left;
        const top       = P3.View.center.y * zoom;
        const bottom    = this._mapHeight - top;
        const x         = clamp(this._cameraTarget.x, left, right);
        const y         = clamp(this._cameraTarget.y, top, bottom);
        const xDiff     = (x - this._camera.target.x) * this._cameraTargetEase * window.dt;
        const yDiff     = (y - this._camera.target.y) * this._cameraTargetEase * window.dt;

        this._camera.target.x += xDiff;
        this._camera.target.y += yDiff;

        this._camera.update();

        this._mapDragDistanceCovered += new P3.Vector2(xDiff, yDiff).length;
    }

    /**
     * @param {string[]} ids
     * @private
     */
    _parseLocations(ids) {
        const lastLocation = common.saveData.activeSlot.player.lastLocation;

        for (let i = 0, count = ids.length; i < count; ++i) {
            const location = ids[i];
            let data;
            try {
                data = json(ids[i]);
            } catch {
                //console.log("[" + this.constructor.name + "]", "Location '" + location + "' not found");
            }

            if (data) {
                const position = new PIXI.Point(data.metadata.mapPosition.x, data.metadata.mapPosition.y);
                
                const button = this.createLocationButton(location, locale(data.metadata.name).toUpperCase(), position);

                if (location === lastLocation) {
                    const pin = new PIXI.Sprite(texture("icon_map_current_location"));
                    pin.x = position.x;
                    pin.y = position.y - button.height + 50;
                    pin.anchor.set(0.5, 1);
                    pin.scale.set(0.75);
                    this._buttonLayer.view.addChild(pin);
                    this._cameraTarget.x = pin.x;
                    this._cameraTarget.y = pin.y;
                    this._buttonLayer.view.addChild(button);

                    this._defaultTween = gsap.timeline();
                    this.addTween(this._defaultTween);
    
                    this._defaultTween.add(gsap.to(pin.position, 0.5, {
                        x: pin.x,
                        y: pin.y - 25,
                        ease: Circ.easeInOut,
                    }), 0);
                }
            }
        }
    }

    /**
     * @param {string} id
     * @param {string} name
     * @param {PIXI.Point} position
     */
    createLocationButton(id, name, position) {
        const button = new LocationButton(name);
        button.position = position;
        button.signals.click.add(() => {
            P3.audio.playSound("vo_bert_huh_00");
            this.signals.next.dispatch(id);
        });
        this._buttonLayer.view.addChild(button);
        return button;
    }

    /**
     * @param {string} name
     */
    createTitleBanner(name) {
        this._titleBanner = new PIXI.Sprite(texture("title_map"));
        this._titleBanner.anchor.set(0.5);
        this._titleBanner.x = common.STAGE_WIDTH/2;
        this._titleBanner.y = 100;
        this.addChild(this._titleBanner);

        this._titleBanner.label = new PIXI.BitmapText(name, {
            fontName: "Alghera Pro",
            fontSize: 52,
            align: "left",
            tint: 0x0
        });
        this._titleBanner.label.position.set(0, -16);
        this._titleBanner.label.anchor.set(0.5);
        this._titleBanner.addChild(this._titleBanner.label);

        resizeLabelToFitText(this._titleBanner.label, 280);
    }

    /**
     * @param {PIXI.Point} parallax
     * @returns {P3.CameraLayer}
     * @private
     */
    _addEmptyLayer(parallax) {
        const container = new PIXI.Container();
        this.addChild(container);

        const layer = new P3.CameraLayer(container, parallax);
        this._camera.addLayer(layer);

        return layer;
    }

    onCloseButtonClick() {
        this.signals.previous.dispatch();
    }
}

/**
 * @static
 * @type {MapScene}
 */
MapScene.active = null;